import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Avatar,
  InputAdornment,
  TextField,
  Chip,
  Tooltip,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PeopleIcon from "@material-ui/icons/People";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import GroupIcon from "@material-ui/icons/Group";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import ForumIcon from "@material-ui/icons/Forum";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },

  /* ---- Header card ---- */
  headerCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #1e1e2f 0%, #2d2d44 100%)"
        : "linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "1.15rem",
  },
  countBadge: {
    fontWeight: 600,
    borderRadius: 20,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },

  /* ---- Search bar ---- */
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: "100%",
    maxWidth: 420,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "#fff",
    },
  },

  /* ---- Table ---- */
  tableContainer: {
    borderRadius: 12,
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  table: {
    minWidth: 500,
  },
  tableHeadRow: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "#f0f2f5",
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
  },
  tableRow: {
    transition: "background-color 0.15s ease",
    "&:nth-of-type(even)": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.02)"
          : "rgba(0,0,0,0.015)",
    },
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.07)"
          : "rgba(0,0,0,0.04)",
    },
  },
  tableCell: {
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  /* ---- Group avatar & name ---- */
  groupInfoCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 40,
    height: 40,
    border: `2px solid ${theme.palette.divider}`,
  },
  avatarFallback: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.primary.light,
    color: "#fff",
  },
  groupName: {
    fontWeight: 600,
    fontSize: "0.9rem",
    lineHeight: 1.3,
    wordBreak: "break-word",
  },

  /* ---- Participant count ---- */
  participantChip: {
    fontWeight: 500,
    fontSize: "0.78rem",
  },

  /* ---- Action buttons ---- */
  actionsCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  actionBtn: {
    transition: "transform 0.15s ease, color 0.15s ease",
    "&:hover": {
      transform: "scale(1.15)",
    },
  },
  viewBtn: {
    color: theme.palette.primary.main,
  },
  linkBtn: {
    color: theme.palette.info.main,
  },
  leaveBtn: {
    color: theme.palette.error.main,
  },

  /* ---- Empty state ---- */
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: 72,
    color: theme.palette.action.disabled,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  emptySubtitle: {
    color: theme.palette.text.disabled,
    maxWidth: 360,
  },

  /* ---- Loading ---- */
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(8),
  },
}));

const GroupsList = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [leaveModal, setLeaveModal] = useState({ open: false, group: null });
  const [leaving, setLeaving] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(null);

  useEffect(() => {
    if (!connectionId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroups = async () => {
      try {
        const { data } = await api.get(
          `/group-management/connections/${connectionId}/groups`
        );
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [connectionId, history]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const term = searchTerm.toLowerCase();
    return groups.filter((g) =>
      (g.subject || g.id).toLowerCase().includes(term)
    );
  }, [groups, searchTerm]);

  const handleBack = () => history.push("/group-management");

  const handleParticipants = (group) => {
    history.push(
      `/group-management/groups/${encodeURIComponent(
        group.id
      )}/participants?connectionId=${connectionId}`
    );
  };

  const handleInviteCode = async (group) => {
    setLoadingInvite(group.id);
    try {
      const { data } = await api.get(
        `/group-management/groups/${encodeURIComponent(
          group.id
        )}/invite-code`,
        { params: { connectionId } }
      );
      const link =
        data.link ||
        (data.code ? `https://chat.whatsapp.com/${data.code}` : "");
      if (link) {
        navigator.clipboard.writeText(link).then(() => {
          toast.success(i18n.t("groupManagement.inviteLinkCopied"));
        });
      } else {
        toast.error(i18n.t("groupManagement.inviteLinkError"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingInvite(null);
    }
  };

  const handleLeave = async () => {
    if (!leaveModal.group) return;
    setLeaving(true);
    try {
      await api.delete(
        `/group-management/groups/${encodeURIComponent(
          leaveModal.group.id
        )}`,
        { params: { connectionId } }
      );
      toast.success(i18n.t("groupManagement.leftGroup"));
      setGroups((prev) =>
        prev.filter((g) => g.id !== leaveModal.group.id)
      );
      setLeaveModal({ open: false, group: null });
    } catch (err) {
      toastError(err);
    } finally {
      setLeaving(false);
    }
  };

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <Button
          className={classes.backButton}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.listGroups")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ---- Header card with count badge ---- */}
            <Paper className={classes.headerCard} elevation={0}>
              <Box className={classes.headerLeft}>
                <GroupIcon className={classes.headerIcon} />
                <Typography className={classes.headerTitle}>
                  {i18n.t("groupManagement.listGroups")}
                </Typography>
              </Box>
              <Chip
                label={groups.length}
                className={classes.countBadge}
                size="small"
              />
            </Paper>

            {/* ---- Search / filter bar ---- */}
            {groups.length > 0 && (
              <Box className={classes.searchContainer}>
                <TextField
                  className={classes.searchField}
                  variant="outlined"
                  size="small"
                  placeholder={i18n.t("groupManagement.searchGroups")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* ---- Table or empty state ---- */}
            {groups.length === 0 ? (
              <Paper className={classes.tableContainer} elevation={0}>
                <Box className={classes.emptyState}>
                  <ForumIcon className={classes.emptyIcon} />
                  <Typography variant="h6" className={classes.emptyTitle}>
                    {i18n.t("groupManagement.noGroups")}
                  </Typography>
                </Box>
              </Paper>
            ) : filteredGroups.length === 0 ? (
              <Paper className={classes.tableContainer} elevation={0}>
                <Box className={classes.emptyState}>
                  <SearchIcon className={classes.emptyIcon} />
                  <Typography variant="h6" className={classes.emptyTitle}>
                    {i18n.t("groupManagement.noGroups")}
                  </Typography>
                </Box>
              </Paper>
            ) : (
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
                elevation={0}
              >
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell className={classes.tableHeadCell}>
                        {i18n.t("groupManagement.groupName")}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align="center"
                      >
                        {i18n.t("groupManagement.participantsCount")}
                      </TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        align="center"
                      >
                        {i18n.t("groupManagement.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGroups.map((group) => (
                      <TableRow key={group.id} className={classes.tableRow}>
                        {/* Group avatar + name */}
                        <TableCell className={classes.tableCell}>
                          <Box className={classes.groupInfoCell}>
                            <Avatar
                              src={group.pictureUrl}
                              className={
                                group.pictureUrl
                                  ? classes.avatar
                                  : classes.avatarFallback
                              }
                              variant="circular"
                            >
                              <GroupIcon />
                            </Avatar>
                            <Typography className={classes.groupName}>
                              {group.subject || group.id}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Participant count */}
                        <TableCell
                          className={classes.tableCell}
                          align="center"
                        >
                          <Chip
                            icon={<PeopleIcon style={{ fontSize: 16 }} />}
                            label={group.participantsCount ?? "-"}
                            size="small"
                            variant="outlined"
                            className={classes.participantChip}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell
                          className={classes.tableCell}
                          align="center"
                        >
                          <Box className={classes.actionsCell}>
                            <Tooltip
                              title={i18n.t(
                                "groupManagement.seeParticipants"
                              )}
                              arrow
                            >
                              <IconButton
                                size="small"
                                className={`${classes.actionBtn} ${classes.viewBtn}`}
                                onClick={() => handleParticipants(group)}
                              >
                                <PeopleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip
                              title={i18n.t(
                                "groupManagement.copyInviteLink"
                              )}
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  className={`${classes.actionBtn} ${classes.linkBtn}`}
                                  onClick={() => handleInviteCode(group)}
                                  disabled={loadingInvite === group.id}
                                >
                                  {loadingInvite === group.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <LinkIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip
                              title={i18n.t(
                                "groupManagement.leaveGroup"
                              )}
                              arrow
                            >
                              <IconButton
                                size="small"
                                className={`${classes.actionBtn} ${classes.leaveBtn}`}
                                onClick={() =>
                                  setLeaveModal({
                                    open: true,
                                    group,
                                  })
                                }
                              >
                                <ExitToAppIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      {/* ---- Confirmation modal for leaving group ---- */}
      <ConfirmationModal
        open={leaveModal.open}
        onClose={() => setLeaveModal({ open: false, group: null })}
        onConfirm={handleLeave}
        title={i18n.t("groupManagement.leaveGroup")}
      >
        {leaveModal.group && (
          <Box>
            <Typography>
              {i18n.t("groupManagement.leaveGroupConfirm", {
                name: leaveModal.group.subject || leaveModal.group.id,
              })}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              {i18n.t("groupManagement.leaveGroupOthersRemain")}
            </Typography>
          </Box>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default GroupsList;
